import { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { CalendarDays, Plus, Trash2, Pencil, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

export default function Holidays() {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [formData, setFormData] = useState({ name: '', date: '', holiday_type: 'public' });
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [filterYear, setFilterYear] = useState('all');

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const canManage = currentUser.role === 'super_admin' || currentUser.role === 'admin';

  useEffect(() => { fetchHolidays(); }, []);

  const fetchHolidays = async () => {
    try {
      const res = await api.get('/holidays');
      setHolidays(res.data.sort((a, b) => a.date.localeCompare(b.date)));
    } catch { toast.error('Failed to load holidays'); }
    finally { setLoading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.date) { toast.error('Name and date are required'); return; }
    try {
      if (editingHoliday) {
        await api.put(`/holidays/${editingHoliday.id}`, formData);
        toast.success('Holiday updated');
      } else {
        await api.post('/holidays', formData);
        toast.success('Holiday added');
      }
      closeDialog();
      fetchHolidays();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save holiday'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/holidays/${id}`);
      toast.success('Holiday removed');
      fetchHolidays();
    } catch { toast.error('Failed to remove holiday'); }
  };

  const openEdit = (h) => {
    setEditingHoliday(h);
    setFormData({ name: h.name, date: h.date, holiday_type: h.holiday_type });
    setIsDialogOpen(true);
  };

  const openCreate = () => {
    setEditingHoliday(null);
    setFormData({ name: '', date: '', holiday_type: 'public' });
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingHoliday(null);
    setFormData({ name: '', date: '', holiday_type: 'public' });
  };

  // Calendar helpers
  const calendarDays = useMemo(() => {
    const firstDay = new Date(calendarYear, calendarMonth, 1).getDay();
    const daysInMonth = new Date(calendarYear, calendarMonth + 1, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  }, [calendarMonth, calendarYear]);

  const holidayMap = useMemo(() => {
    const map = {};
    holidays.forEach(h => { map[h.date] = h; });
    return map;
  }, [holidays]);

  const getHolidayForDay = (day) => {
    if (!day) return null;
    const key = `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return holidayMap[key] || null;
  };

  const prevMonth = () => {
    if (calendarMonth === 0) { setCalendarMonth(11); setCalendarYear(y => y - 1); }
    else setCalendarMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (calendarMonth === 11) { setCalendarMonth(0); setCalendarYear(y => y + 1); }
    else setCalendarMonth(m => m + 1);
  };

  // Year filter
  const availableYears = useMemo(() => {
    const years = new Set(holidays.map(h => h.date.substring(0, 4)));
    years.add(String(new Date().getFullYear()));
    return [...years].sort();
  }, [holidays]);

  const filteredHolidays = filterYear === 'all' ? holidays : holidays.filter(h => h.date.startsWith(filterYear));
  const now = new Date();
  const upcoming = filteredHolidays.filter(h => new Date(h.date) >= now);
  const companyCount = filteredHolidays.filter(h => h.holiday_type === 'company').length;

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="p-8 min-h-full bg-slate-50">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-1">Holiday Calendar</h1>
          <p className="text-slate-500">Manage public and company holidays</p>
        </div>
        {canManage && (
          <Button className="bg-[#5B6FD6] hover:bg-[#4a5bc2]" onClick={openCreate} data-testid="add-holiday-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Holiday
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-6">
        <Card className="border-slate-200 shadow-sm"><CardContent className="p-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-[#5B6FD6]/10 flex items-center justify-center"><CalendarDays className="h-5 w-5 text-[#5B6FD6]" /></div>
          <div><p className="text-2xl font-bold">{filteredHolidays.length}</p><p className="text-xs text-slate-500">Total Holidays</p></div>
        </CardContent></Card>
        <Card className="border-slate-200 shadow-sm"><CardContent className="p-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center"><CalendarDays className="h-5 w-5 text-green-600" /></div>
          <div><p className="text-2xl font-bold">{upcoming.length}</p><p className="text-xs text-slate-500">Upcoming</p></div>
        </CardContent></Card>
        <Card className="border-slate-200 shadow-sm"><CardContent className="p-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center"><CalendarDays className="h-5 w-5 text-slate-500" /></div>
          <div><p className="text-2xl font-bold">{companyCount}</p><p className="text-xs text-slate-500">Company Holidays</p></div>
        </CardContent></Card>
      </div>

      {/* Calendar View */}
      <Card className="border-slate-200 shadow-sm mb-6" data-testid="holiday-calendar">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={prevMonth} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <CardTitle className="text-lg font-semibold min-w-[180px] text-center">{MONTHS[calendarMonth]} {calendarYear}</CardTitle>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={nextMonth} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
            <Button variant="outline" size="sm" onClick={() => { setCalendarMonth(new Date().getMonth()); setCalendarYear(new Date().getFullYear()); }}>Today</Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-lg overflow-hidden">
            {WEEKDAYS.map(d => (
              <div key={d} className="bg-slate-50 text-center py-2 text-xs font-semibold text-slate-500 uppercase">{d}</div>
            ))}
            {calendarDays.map((day, i) => {
              const holiday = getHolidayForDay(day);
              const isToday = day && calendarYear === now.getFullYear() && calendarMonth === now.getMonth() && day === now.getDate();
              const cellKey = day ? `d-${calendarYear}-${calendarMonth}-${day}` : `e-${i}`;
              return (
                <div key={cellKey} className={`bg-white min-h-[70px] p-1.5 ${!day ? 'bg-slate-50/50' : 'hover:bg-slate-50'} ${isToday ? 'ring-2 ring-inset ring-[#5B6FD6]' : ''}`}>
                  {day && (
                    <>
                      <span className={`text-xs font-medium ${isToday ? 'text-[#5B6FD6] font-bold' : 'text-slate-600'}`}>{day}</span>
                      {holiday && (
                        <div
                          className={`mt-0.5 px-1 py-0.5 rounded text-[10px] leading-tight truncate cursor-pointer ${holiday.holiday_type === 'public' ? 'bg-red-100 text-red-700' : 'bg-[#5B6FD6]/10 text-[#5B6FD6]'}`}
                          title={holiday.name}
                          onClick={() => canManage && openEdit(holiday)}
                          data-testid={`cal-holiday-${holiday.id}`}
                        >
                          {holiday.name}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Holiday List with Year Filter */}
      <Card className="border-slate-200 shadow-sm" data-testid="holiday-list">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold">All Holidays</CardTitle>
            <Select value={filterYear} onValueChange={setFilterYear}>
              <SelectTrigger className="w-32" data-testid="year-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {availableYears.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="grid grid-cols-12 gap-2 px-5 py-2 bg-slate-50 border-y border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            <div className="col-span-4">Holiday</div>
            <div className="col-span-3">Date</div>
            <div className="col-span-2">Day</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          {filteredHolidays.length === 0 ? (
            <div className="py-10 text-center text-slate-400">No holidays found</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredHolidays.map(h => {
                const d = new Date(h.date + 'T00:00:00');
                const isPast = d < now;
                return (
                  <div key={h.id} className={`grid grid-cols-12 gap-2 items-center px-5 py-3 hover:bg-slate-50 ${isPast ? 'opacity-50' : ''}`} data-testid={`holiday-${h.id}`}>
                    <div className="col-span-4 font-medium text-sm text-slate-900">{h.name}</div>
                    <div className="col-span-3 text-sm text-slate-600">
                      {d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                    <div className="col-span-2 text-sm text-slate-500">
                      {d.toLocaleDateString('en-IN', { weekday: 'long' })}
                    </div>
                    <div className="col-span-2">
                      <Badge className={h.holiday_type === 'public' ? 'bg-red-100 text-red-700' : 'bg-[#5B6FD6]/10 text-[#5B6FD6]'}>
                        {h.holiday_type === 'public' ? 'Public' : 'Company'}
                      </Badge>
                    </div>
                    <div className="col-span-1 flex justify-end gap-1">
                      {canManage && (
                        <>
                          <button onClick={() => openEdit(h)} className="p-1.5 text-slate-400 hover:text-[#5B6FD6] hover:bg-[#5B6FD6]/10 rounded-lg" data-testid={`edit-holiday-${h.id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => handleDelete(h.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg" data-testid={`delete-holiday-${h.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={closeDialog}>
        <DialogContent className="max-w-md" data-testid="holiday-dialog">
          <DialogHeader>
            <DialogTitle>{editingHoliday ? 'Edit Holiday' : 'Add Holiday'}</DialogTitle>
            <DialogDescription className="sr-only">{editingHoliday ? 'Edit an existing holiday' : 'Add a new holiday to the calendar'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <Label>Holiday Name <span className="text-red-500">*</span></Label>
              <Input placeholder="e.g., Republic Day" value={formData.name}
                onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                data-testid="holiday-name-input" required />
            </div>
            <div>
              <Label>Date <span className="text-red-500">*</span></Label>
              <Input type="date" value={formData.date}
                onChange={(e) => setFormData(p => ({ ...p, date: e.target.value }))}
                data-testid="holiday-date-input" required />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={formData.holiday_type} onValueChange={(v) => setFormData(p => ({ ...p, holiday_type: v }))}>
                <SelectTrigger data-testid="holiday-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public Holiday</SelectItem>
                  <SelectItem value="company">Company Holiday</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={closeDialog}>Cancel</Button>
              <Button type="submit" className="bg-[#5B6FD6] hover:bg-[#4a5bc2]" data-testid="save-holiday-btn">
                {editingHoliday ? 'Update Holiday' : 'Add Holiday'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
